import React, { useState } from "react";
import AppLayout from "../../../layouts/AppLayout";
import { Button, DatePicker, Dropdown, Input, Select, TimePicker } from "antd";
import BottomDropdownIcon from "../../../assets/icons/BottomDropdownIcon";
import IconButton from "../../../components/ui/IconButton";
import dayjs from "dayjs";
import { API_URL, dbDateFormat } from "../../../constants/constants";
import { disableFutureDates } from "../../../lib/time";
import { GetSupplierPaymentHistoryParams } from "../../../types/statements";
import useTable from "../../../hooks/useTable";
import SearchIcon from "../../../assets/icons/SearchIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { addKeyInArray, customTimeConverter } from "../../../utils/utils";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useGetDealerListQuery } from "../../../features/dealer/dealerApi";
import useError from "../../../hooks/useError";
import emptyUser from "../../../assets/images/empty_user.jpg";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import { BASE_API } from "../../../constants/apiUrls";
import { GetDealerListResponseType } from "../../../types/dealer.type";
import { capitalize } from "lodash";
import { skipData } from "../../../utils/number";

type TimeFormat = {
  formTime?: string;
  toTime?: string;
};

export default function DealerList() {
  const [time, setTime] = useState<TimeFormat>();

  const [openDropdown, setOpenDropdown] = useState(false);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetSupplierPaymentHistoryParams>();
  const endTime = time?.toTime
    ? customTimeConverter(dayjs(time?.toTime, "HH:mm:ss").format("hh:mm:ss A"))
    : undefined;

  const {
    data: dealerList,
    isLoading: isDealerloading,
    isError: isDealerError,
    error: dealerError,
  } = useGetDealerListQuery({
    ...filter,
    search: keyword,
    startDate: filter?.startDate
      ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
      : undefined,
    endDate: filter?.endDate
      ? `${filter?.endDate} ${endTime || "23:59:59"}`
      : undefined,
  });
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  useError(isDealerError, dealerError, "Get dealer list data fail");

  const handleStatementCancel = () => {
    handleFilter("startDate", undefined);
    handleFilter("endDate", undefined);
    setTime(undefined);
  };

  const DateTimeSelector = (
    <div className="p-6 border rounded-lg shadow-md max-w-md mx-auto bg-white clear-button-hide">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 mb-1">From Date</label>
          <DatePicker
            value={
              filter?.startDate ? dayjs(filter?.startDate, dbDateFormat) : null
            }
            onChange={(e) =>
              handleFilter("startDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.formTime ? dayjs(time?.formTime, "HH:mm:ss") : null}
            onChange={(e) =>
              setTime((prev) => ({
                ...prev,
                formTime: dayjs(e).format("HH:mm:ss"),
              }))
            }
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">To Date</label>
          <DatePicker
            value={
              filter?.endDate ? dayjs(filter?.endDate, dbDateFormat) : null
            }
            onChange={(e) =>
              handleFilter("endDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.toTime ? dayjs(time?.toTime, "HH:mm:ss") : null}
            onChange={(e) => {
              setTime((prev) => ({
                ...prev,
                toTime: dayjs(e).format("HH:mm:ss"),
              }));
            }}
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button className="text-gray-600" onClick={handleStatementCancel}>
          Cancel
        </Button>
        {/* <Button
              type="primary"
              className="!text-primary !bg-[#147AFF33]"
              onClick={() => setOpenDropdown(false)}
            >
              View Statement
            </Button> */}
      </div>
    </div>
  );

  const columns: ColumnsType<GetDealerListResponseType> = [
    {
      title: <div className="text-nowrap">Dealer Code</div>,
      dataIndex: "dealerNo",
      align: "center",
      render: (val) => {
        return val ? val : "-";
      },
    },
    {
      title: <div style={{ width: "200px" }}>Dealer Name</div>,
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={emptyUser}
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.name}</span>
          </div>
        );
      },
    },
    {
      title: "Zone",
      dataIndex: "zone",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: "District",
      dataIndex: "district",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Wallet Number</div>,
      dataIndex: "merchantMsisdn",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Dealer Transaction</div>,
      dataIndex: "dealerTransaction",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Combined Transaction</div>,
      dataIndex: "combinedTransaction",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },

    {
      title: <div className="text-nowrap">Onboard Date</div>,
      key: "date",
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("DD/MM/YYYY") : "-"}
          </span>
        );
      },
    },
  ];

  const handleDownloadDealers = () => {
    downloadXlsx(
      API_URL + BASE_API.getDealerList,
      {
        ...filter,
        search: keyword,
        startDate: filter?.startDate
          ? `${filter?.startDate} ${time?.formTime || "00:00:00"}`
          : undefined,
        endDate: filter?.endDate
          ? `${filter?.endDate} ${endTime || "23:59:59"}`
          : undefined,
        skip: 0,
        limit: 100000000000,
      },
      `sPay Statement_dealers_list${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "rows",
      (data: GetDealerListResponseType["data"]["rows"][0]) => {
        return {
          "Dealer Code": data?.dealerNo ?? "-",
          "Dealer Name": data?.name ?? "-",
          Zone: data?.zone ?? "-",
          District: data?.district ?? "-",
          "Wallet Number": data?.merchantMsisdn ?? "-",
          "Dealer Transaction": "-",
          "Combined Transaction": "-",
          "Onboard Date": data?.createdAt
            ? moment(data?.createdAt).utc().format("DD/MM/YYYY")
            : "-",
        };
      }
    );
  };

  return (
    <>
      <AppLayout title="Dealear List">
        <div className="self-stretch justify-start !text-[#2D3643] text-4xl font-medium mb-5">
          Dealer List
        </div>
        <div className="flex justify-between items-center ">
          <div className="flex justify-start items-center gap-3">
            {/* <Dropdown
              overlay={DateTimeSelector}
              trigger={["click"]}
              open={openDropdown}
              onOpenChange={setOpenDropdown}
            >
              <IconButton
                className="flex items-center text-[#7a8799]"
                // nextIcon={<BottomDropdownIcon color="#7a8799" />}
              >
                Onboard Date
              </IconButton>
            </Dropdown> */}

            {/* <Select
              placeholder="Zone"
              suffixIcon={<BottomDropdownIcon color="#7a8799" />}
              // onChange={(val) => handleFilter("spayStatus", val)}
              className="min-w-[100px]"
              allowClear
            /> */}

            {/* <Select
              placeholder="District"
              suffixIcon={<BottomDropdownIcon color="#7a8799" />}
              // onChange={(val) => handleFilter("spayStatus", val)}
              className="min-w-[100px]"
              allowClear
            /> */}
          </div>

          <div className="flex justify-start items-center gap-3">
            <Input
              placeholder="Search dealer’s name or code"
              className="text-[#AFBACA] !w-[280px]"
              prefix={<SearchIcon />}
              onChange={handleSearch}
              allowClear
            />
            <Button
              className="!text-primary bg-[#147AFF33]"
              loading={excelLoading}
              onClick={handleDownloadDealers}
            >
              Export
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <CustomTable
            columns={columns}
            className="text-header overflow-auto mt-4"
            loading={isDealerloading}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            limit={limit}
            dataList={addKeyInArray(dealerList?.data?.rows)}
            page={page}
            bordered={false}
            total={dealerList?.data?.count || 0}
            hideSpace
            paginationClassnames="dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight py-4 px-4 relative"
            // showTotalPage
          />
        </div>
      </AppLayout>
    </>
  );
}
